import {
  KeyRound,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Eye,
  EyeOff,
} from "lucide-react";
import React, { useState } from "react";
import { GlassStyle } from "../types";

interface ApiKeyInputProps {
  glassStyle: GlassStyle;
  apiKey: string;
  setApiKey: (key: string) => void;
}

const ApiKeyInput: React.FC<ApiKeyInputProps> = ({
  glassStyle,
  apiKey,
  setApiKey,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [showKey, setShowKey] = useState(false);

  // Toggle function to open or close the form
  const toggleAccordion = () => setIsOpen(!isOpen);

  const checkApiKey = () => {
    return apiKey?.includes("tvly-") && apiKey?.length >= 32;
  };

  return (
    <div className="relative">
      {/* Main Card for API Keys */}
      <div
        className={`${glassStyle.card} backdrop-blur-2xl bg-white/90 border-gray-200/50 shadow-xl cursor-pointer`}
        style={{ padding: "10px 24px" }}
      >
        {/* Header with a button to toggle the form visibility */}
        <div
          className="flex justify-between items-center py-4 cursor-pointer"
          onClick={toggleAccordion} // Toggle on header click
        >
          <div className="flex items-center gap-3">
            <span className="text-xl font-medium text-gray-700">
              Tavily API Key
            </span>
            <span className="text-xs font-medium text-gray-500 px-2 py-1 bg-gray-100 rounded-md mt-0.5">
              required
            </span>
            {checkApiKey() && (
              <CheckCircle2 className="h-5 w-5 mt-0.5 text-[#22C55E]" />
            )}
          </div>
          <span className="text-xl text-gray-500">
            {isOpen ? <ChevronUp /> : <ChevronDown />}
            {/* Show up/down arrow based on state */}
          </span>
        </div>

        {/* Form Fields (conditionally rendered based on `isOpen` state) */}
        {isOpen && (
          <div className="space-y-6 pb-3 transition-all duration-500 ease-in-out">
            <div className="relative group">
              <div className="relative">
                <KeyRound
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 stroke-[#468BFF] transition-all duration-200 group-hover:stroke-[#8FBCFA] z-10"
                  strokeWidth={1.5}
                />
                <input
                  required
                  id="tavilyApiKey"
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  spellCheck="false"
                  onChange={(e) => setApiKey(e.target.value)}
                  className={`${glassStyle.input} transition-all duration-300 focus:border-[#468BFF]/50 focus:ring-1 focus:ring-[#468BFF]/50 group-hover:border-[#468BFF]/30 bg-white/80 backdrop-blur-sm text-lg py-4 pl-12 pr-12 font-['DM_Sans']`}
                  placeholder="Enter Tavily API Key"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#468BFF] hover:text-[#8FBCFA] focus:outline-none z-10"
                >
                  {showKey ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            <div className="flex align-center">
              <p className="text-sm text-gray-600">
                Each report generation will use approx. 30 API credits.
              </p>

              <a
                href="https://app.tavily.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#468BFF] hover:text-blue-300 px-1"
              >
                Don't have an API key?
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeyInput;
